import { Badge, Button, Callout, Card, DropdownMenu, ScrollArea, Text, Tooltip } from '@radix-ui/themes';
import { CaretDownIcon, CheckIcon, CodeIcon, InfoCircledIcon, PaperPlaneIcon, PlusIcon, QuoteIcon, TrashIcon } from '@radix-ui/react-icons';
import { generateId } from '../../util/utils';
import { useAuthStore, useEditorStore, useMovieStore, type ChatMessage, type ChatConversation } from '../../stores';
import { authFetch } from '../../util/authFetch';
import { handleUnauthorizedRedirect } from '../../util/authRedirect';
import './Editor.css';
import { TextArea } from '@radix-ui/themes';
import { useState, useEffect, useRef, useCallback, memo, lazy, Suspense } from 'react';
import { useGet, useAiActionExecutor } from '../../hooks';
import { PersonIcon } from '@radix-ui/react-icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useThemeStore } from '../../stores';
import { AIAssistantMessage } from './AIAssistantMessage';
import { parseAssistantResponse } from '../../util/aiResponseParser';
import { ensureMovieForChat } from '../../util/ensureMovieForChat';
import { sendChatMessageDirect } from '../../util/aiChatClient';
import { processAiProviderResponse } from '../../util/processAiProviderResponse';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useTranslation } from '../../i18n';
import { ChatPromptWalker } from './ChatPromptWalker';

// Lazy load syntax highlighter to reduce initial bundle size
const SyntaxHighlighter = lazy(() =>
    import('react-syntax-highlighter').then(mod => ({ default: mod.Prism }))
);

/** Memoized chat message component to prevent unnecessary re-renders */
const ChatMessageItem = memo(({
    message,
    showDebug,
    onDelete,
    isDeleting,
}: {
    message: ChatMessage;
    showDebug: boolean;
    onDelete?: (messageId: string) => void;
    isDeleting?: boolean;
}) => {
    const { t } = useTranslation();
    const codeStyle = useThemeStore((state) => state.resolvedAppearance) === 'dark' ? oneDark : oneLight;
    const canDelete = Boolean(onDelete) && !isDeleting;
    const deleteButton = canDelete ? (
        <Tooltip content={t('aiChat.deleteMessage')}>
            <Button
                variant="ghost"
                size="1"
                className="user-chat-message-delete"
                aria-label={t('aiChat.deleteMessage')}
                onClick={() => onDelete?.(message.id)}
            >
                <TrashIcon aria-hidden="true" />
            </Button>
        </Tooltip>
    ) : null;

    if (message.role === 'assistant') {
        const parsed = parseAssistantResponse(message.content);
        const debug = message.debug;
        const jobs = debug?.jobs ?? (showDebug ? parsed.jobs : []);
        const actions = debug?.actions ?? (showDebug ? parsed.actions : []);
        const payloads = debug?.payloads ?? (showDebug ? parsed.payloads : []);
        const executionResults = debug?.executionResults;
        const { thinking, response } = parsed;
        return (
            <div className='user-chat-message user-chat-message-assistant'>
                <div className='flex items-start gap-2 p-2 min-w-0 flex-1'>
                <Badge color="gray" size="3">AI</Badge>
                <div className='flex flex-col gap-1 min-w-0'>
                    {thinking && thinking.length > 0 && (
                        <details className='ai-think-block'>
                            <summary className='ai-think-summary'>{t('aiChat.thinking')}</summary>
                            <div className='ai-think-content'>{thinking}</div>
                        </details>
                    )}
                    {showDebug && jobs.length > 0 && (
                        <details className='ai-think-block' open>
                            <summary className='ai-jobs-summary'>{t('aiChat.jobs')}</summary>
                            {jobs.map((job, index) => (
                                <pre key={`job-${index}`} className='ai-jobs-content'>
                                    {JSON.stringify(job, null, 2)}
                                </pre>
                            ))}
                        </details>
                    )}
                    {showDebug && actions.length > 0 && (
                        <details className='ai-think-block' open>
                            <summary className='ai-actions-summary'>{t('aiChat.actions')}</summary>
                            {actions.map((action, index) => (
                                <pre key={`action-${index}`} className='ai-actions-content'>
                                    {JSON.stringify(action, null, 2)}
                                </pre>
                            ))}
                        </details>
                    )}
                    {showDebug && payloads.length > 0 && (
                        <details className='ai-think-block' open>
                            <summary className='ai-payloads-summary'>{t('aiChat.payloads')}</summary>
                            {payloads.map((payload, index) => (
                                <pre key={`payload-${index}`} className='ai-payloads-content'>
                                    {JSON.stringify(payload, null, 2)}
                                </pre>
                            ))}
                        </details>
                    )}
                    {showDebug && executionResults && executionResults.length > 0 && (
                        <details className='ai-think-block' open>
                            <summary className='ai-debug-summary'>{t('aiChat.executionResults')}</summary>
                            {executionResults.map((result, index) => (
                                <pre key={`exec-${index}`} className='ai-debug-content'>
                                    {JSON.stringify(result, null, 2)}
                                </pre>
                            ))}
                        </details>
                    )}
                    <div className='ai-markdown-content text-theme-muted text-sm'>
                        <Suspense fallback={<div className='text-theme-muted text-xs'>{t('aiChat.loading')}</div>}>
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    code({ className, children, ...props }) {
                                        const match = /language-(\w+)/.exec(className || '');
                                        const codeString = String(children).replace(/\n$/, '');
                                        if (match) {
                                            return (
                                                <Suspense fallback={<pre className='text-xs'>{codeString}</pre>}>
                                                    <SyntaxHighlighter
                                                        style={codeStyle}
                                                        language={match[1]}
                                                        PreTag="div"
                                                        wrapLongLines
                                                        customStyle={{
                                                            margin: '0.5em 0',
                                                            borderRadius: '6px',
                                                            fontSize: '0.8rem',
                                                            overflowX: 'hidden',
                                                            whiteSpace: 'pre-wrap',
                                                            wordBreak: 'break-word',
                                                            overflowWrap: 'anywhere',
                                                            maxWidth: '100%',
                                                        }}
                                                    >
                                                        {codeString}
                                                    </SyntaxHighlighter>
                                                </Suspense>
                                            );
                                        }
                                        return (
                                            <code className='ai-inline-code' {...props}>
                                                {children}
                                            </code>
                                        );
                                    },
                                    pre({ children }) {
                                        return <>{children}</>;
                                    },
                                }}
                            >
                                {response}
                            </ReactMarkdown>
                        </Suspense>
                    </div>
                </div>
                </div>
                {deleteButton}
            </div>
        );
    }
    return (
        <div className='user-chat-message user-chat-message-user'>
            <div className='flex items-center gap-2 p-2 min-w-0 flex-1'>
            <PersonIcon className='w-4 h-4 text-theme-primary shrink-0' />
            <span className='text-theme-secondary text-sm'>{message.content}</span>
            </div>
            {deleteButton}
        </div>
    );
});


interface ChatHistoryResponse {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: string;
}

interface ChatConversationResponse {
    id: number;
    title: string | null;
    createdAt: string;
    lastMessageAt: string | null;
}

export function AIChat() {
    const { t } = useTranslation();
    const { 
        aiChatVisible, 
        aiChatText,       
        setAiChatText, 
        aiChatHistory,
        aiChatMovieId,
        aiChatWidth,
        setAiChatWidth,
        addAiChatMessage,
        removeAiChatMessage,
        setAiChatHistory,
        resetAiChat,
        setAiChatMovieId,
        aiChatConversations,
        aiChatActiveConversationId,
        setAiChatConversations,
        setAiChatActiveConversationId,
        addAiChatConversation,
        removeAiChatConversation,
        aiSamplePrompts,
        aiChatDebugMode,
        toggleAiChatDebugMode,
    } = useEditorStore();
    const { selectedMovieId, currentScene } = useMovieStore();
    const currentFrame = useEditorStore((state) => state.currentFrame);
    const isSystemUser = useAuthStore((state) => state.user?.role === 'system');
    const showDebug = aiChatDebugMode || isSystemUser;
    const [isResizing, setIsResizing] = useState(false);
    const [isEnsuringMovie, setIsEnsuringMovie] = useState(false);
    const [chatError, setChatError] = useState<string | null>(null);
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);
    const { executeActions } = useAiActionExecutor();
    const sidebarRef = useRef<HTMLDivElement>(null);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const prevMovieIdRef = useRef<number | null>(aiChatMovieId);

    // Handle movie changes
    useEffect(() => {
        if (selectedMovieId === null) {
            resetAiChat();
            prevMovieIdRef.current = null;
            return;
        }

        if (selectedMovieId !== prevMovieIdRef.current) {
            const previousMovieId = prevMovieIdRef.current;
            prevMovieIdRef.current = selectedMovieId;
            const { aiChatMovieId: cachedMovieId, aiChatConversations: cachedConvs, aiChatHistory } =
                useEditorStore.getState();
            if (cachedMovieId === selectedMovieId && cachedConvs.length > 0) {
                return;
            }
            // Movie yokken başlayan sohbet: yeni movie bağlanırken mesajları koru
            if (previousMovieId === null && aiChatHistory.length > 0) {
                setAiChatMovieId(selectedMovieId);
                return;
            }
            resetAiChat();
            setAiChatMovieId(selectedMovieId);
        }
    }, [selectedMovieId, resetAiChat, setAiChatMovieId]);

    // Fetch conversations list for this movie (useGet clears stale data on URL change)
    const conversationsUrl = selectedMovieId
        ? `/api/movies/${selectedMovieId}/chat/conversations`
        : null;
    const { data: conversationsData, loading: isLoadingConversations } = useGet<ChatConversationResponse[]>(conversationsUrl);

    // Load conversations from API
    useEffect(() => {
        if (conversationsData && selectedMovieId) {
            const conversations: ChatConversation[] = conversationsData.map(c => ({
                id: c.id,
                title: c.title,
                createdAt: c.createdAt,
                lastMessageAt: c.lastMessageAt,
            }));
            setAiChatConversations(conversations);
            setAiChatMovieId(selectedMovieId);

            // Auto-select the first conversation if none is active or active one no longer exists
            const { aiChatActiveConversationId: activeId } = useEditorStore.getState();
            const activeExists = conversations.some(c => c.id === activeId);
            if ((!activeId || !activeExists) && conversations.length > 0) {
                setAiChatActiveConversationId(conversations[0].id);
            }
        }
    }, [conversationsData, selectedMovieId, setAiChatConversations, setAiChatMovieId, setAiChatActiveConversationId]);

    // Fetch history for the active conversation (URL change auto-triggers fetch)
    const chatHistoryUrl = selectedMovieId && aiChatActiveConversationId
        ? `/api/movies/${selectedMovieId}/chat/history?conversationId=${aiChatActiveConversationId}`
        : null;
    const { data: chatHistoryData, loading: isLoadingHistory } = useGet<ChatHistoryResponse[]>(chatHistoryUrl);

    // Load chat history from API response
    useEffect(() => {
        if (!selectedMovieId || !chatHistoryData || aiChatActiveConversationId === null) {
            return;
        }
        const history = chatHistoryData.map(msg => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.timestamp),
        }));
        setAiChatHistory(history, selectedMovieId);
    }, [chatHistoryData, aiChatActiveConversationId, setAiChatHistory, selectedMovieId]);

    const [isSending, setIsSending] = useState(false);
    const [deletingMessageId, setDeletingMessageId] = useState<string | null>(null);
    const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<string | null>(null);

    const handleRequestDeleteMessage = useCallback((messageId: string) => {
        if (deletingMessageId) return;
        setDeleteConfirmMessageId(messageId);
    }, [deletingMessageId]);

    const handleConfirmDeleteMessage = useCallback(async () => {
        const messageId = deleteConfirmMessageId;
        if (!messageId || !selectedMovieId || aiChatActiveConversationId === null || deletingMessageId) return;

        const numericId = Number.parseInt(messageId, 10);
        if (!Number.isFinite(numericId)) return;

        setDeletingMessageId(messageId);
        setDeleteConfirmMessageId(null);
        try {
            const url = `/api/movies/${selectedMovieId}/chat/conversations/${aiChatActiveConversationId}/messages/${numericId}`;
            const response = await authFetch(url, { method: 'DELETE' });
            if (response.status === 401) {
                handleUnauthorizedRedirect();
                return;
            }
            if (response.ok) {
                removeAiChatMessage(messageId);
            } else {
                console.error('Failed to delete message:', response.status);
            }
        } catch (error) {
            console.error('Failed to delete message:', error);
        } finally {
            setDeletingMessageId(null);
        }
    }, [deleteConfirmMessageId, selectedMovieId, aiChatActiveConversationId, deletingMessageId, removeAiChatMessage]);

    const deleteConfirmMessage = deleteConfirmMessageId
        ? aiChatHistory.find((m) => m.id === deleteConfirmMessageId)
        : null;
    const deleteConfirmPreview = deleteConfirmMessage
        ? (deleteConfirmMessage.role === 'assistant'
            ? parseAssistantResponse(deleteConfirmMessage.content).response
            : deleteConfirmMessage.content).trim().slice(0, 120)
        : '';

    const handleArchiveConversation = useCallback(async (e: React.MouseEvent, convId: number) => {
        e.stopPropagation();
        if (!selectedMovieId) return;

        try {
            const url = `/api/movies/${selectedMovieId}/chat/conversations/${convId}`;
            // Use fetch directly for dynamic URL delete
            const response = await authFetch(url, { method: 'PUT' });
            if (response.status === 401) {
                handleUnauthorizedRedirect();
                return;
            }
            if (response.ok) {
                removeAiChatConversation(convId);
            }
        } catch (error) {
            console.error('Failed to delete conversation:', error);
        }
    }, [selectedMovieId, removeAiChatConversation]);

    const handleTabClick = useCallback((convId: number) => {
        if (convId === aiChatActiveConversationId) return;
        setAiChatActiveConversationId(convId);
        setAiChatHistory([], selectedMovieId); // Clear while loading
    }, [aiChatActiveConversationId, setAiChatActiveConversationId, setAiChatHistory, selectedMovieId]);

    const handleNewChat = useCallback(() => {
        setAiChatActiveConversationId(null);
        setAiChatHistory([], selectedMovieId);
        setAiChatText('');
    }, [setAiChatActiveConversationId, setAiChatHistory, selectedMovieId, setAiChatText]);

    // Auto-scroll to bottom
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ block: 'end' });
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            setTimeout(scrollToBottom, 500);
        });
    }, [aiChatHistory, isSending, aiChatVisible]);

    // Resize logic
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (isResizing) {
                const dx = e.clientX - resizeStartX;
                const newWidth = Math.max(200, Math.min(800, resizeStartWidth + dx));
                setAiChatWidth(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, resizeStartX, resizeStartWidth, setAiChatWidth]);

    const handleResizeMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        setResizeStartX(e.clientX);
        setResizeStartWidth(aiChatWidth);
    };

    const sendPromptToAI = async () => {
        if (!aiChatText.trim() || isSending || isEnsuringMovie) return;

        const messageText = aiChatText.trim();
        setAiChatText('');
        setChatError(null);

        const userMessageId = generateId('user-msg');
        addAiChatMessage({
            id: userMessageId,
            role: 'user',
            content: messageText,
            timestamp: new Date(),
        });

        setIsEnsuringMovie(true);
        let movieId: number | null = null;
        try {
            movieId = await ensureMovieForChat(messageText);
        } catch (error) {
            console.error('[AIChat] ensureMovieForChat failed:', error);
        } finally {
            setIsEnsuringMovie(false);
        }

        if (!movieId) {
            setChatError('Could not create or load a project. Please try again.');
            return;
        }

        setIsSending(true);
        try {
            const sceneId = currentScene?.id ? parseInt(currentScene.id, 10) : null;
            const response = await sendChatMessageDirect(
                movieId,
                {
                    message: messageText,
                    conversationId: aiChatActiveConversationId,
                    sceneId: Number.isFinite(sceneId) ? sceneId : null,
                    currentFrame,
                }
            );

            if (response) {
                const assistantMessage = await processAiProviderResponse(response, {
                    captureDebug: showDebug,
                    executeActions,
                });
                addAiChatMessage(assistantMessage);

                if (aiChatActiveConversationId === null && response.conversationId) {
                    const newConv: ChatConversation = {
                        id: response.conversationId,
                        title: response.conversationTitle,
                        createdAt: new Date().toISOString(),
                        lastMessageAt: new Date().toISOString(),
                    };
                    addAiChatConversation(newConv);
                    setAiChatActiveConversationId(response.conversationId);
                }
            } else {
                setChatError('Failed to send message to AI.');
            }
        } catch (error) {
            console.error('Failed to send message to AI:', error);
            setChatError('Failed to send message to AI.');
        } finally {
            setIsSending(false);
        }
    };

    // Calculate dynamic rows based on text length
    const calculateRows = (text: string): number => {
        const baseRows = 3;
        if (text.length > 500) {
            return baseRows * 2;
        }
        return baseRows;
    };

    // Check if we're in "new chat" mode (no active conversation)
    const isNewChat = aiChatActiveConversationId === null;
    const isLoadingChat = isLoadingConversations || isLoadingHistory || isEnsuringMovie;
    const activeConversationTitle = isNewChat
        ? 'New Chat'
        : (aiChatConversations.find((c) => c.id === aiChatActiveConversationId)?.title || 'New Chat');
    const showConversationDropdown = selectedMovieId !== null || aiChatConversations.length > 0;

    if (!aiChatVisible) return null;

    return (
        <div
            ref={sidebarRef}
            className="user-chat-sidebar"
            style={{ width: `${aiChatWidth}px` }}
        >
            <div
                className={`user-chat-resize-handle${isResizing ? ' is-resizing' : ''}`}
                onMouseDown={handleResizeMouseDown}
                role="separator"
                aria-orientation="vertical"
                aria-label={t('aiChat.resizePanel')}
            />
            <div className='user-chat-header'>
                {showConversationDropdown ? (
                    <div className="user-chat-conversation-dropdown">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                            <button
                                type="button"
                                className="user-chat-conversation-trigger"
                                aria-label={t('aiChat.selectConversation')}
                            >
                                <span className="user-chat-conversation-trigger-title">{activeConversationTitle}</span>
                                <CaretDownIcon aria-hidden="true" className="user-chat-conversation-trigger-chevron" />
                            </button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content
                            variant="soft"
                            size="2"
                            align="start"
                            side="bottom"
                            sideOffset={0}
                            collisionPadding={8}
                            className="user-chat-conversation-menu"
                        >
                            <DropdownMenu.Item
                                className={isNewChat ? 'user-chat-conversation-item-active' : undefined}
                                onSelect={handleNewChat}
                            >
                                {isNewChat && <CheckIcon aria-hidden="true" className="user-chat-conversation-check" />}
                                <span className="user-chat-conversation-item-title">{t('aiChat.newChat')}</span>
                            </DropdownMenu.Item>
                            {aiChatConversations.length > 0 && <DropdownMenu.Separator />}
                            {aiChatConversations.map((conv: ChatConversation) => {
                                const isActive = conv.id === aiChatActiveConversationId;
                                return (
                                    <DropdownMenu.Item
                                        key={conv.id}
                                        className={isActive ? 'user-chat-conversation-item-active' : undefined}
                                        onSelect={() => handleTabClick(conv.id)}
                                    >
                                        {isActive && <CheckIcon aria-hidden="true" className="user-chat-conversation-check" />}
                                        <span className="user-chat-conversation-item-title">{conv.title || 'New Chat'}</span>
                                        <button
                                            type="button"
                                            className="user-chat-conversation-item-delete"
                                            aria-label={t('aiChat.deleteConversation')}
                                            onPointerDown={(e) => e.preventDefault()}
                                            onClick={(e) => void handleArchiveConversation(e, conv.id)}
                                        >
                                            <TrashIcon aria-hidden="true" />
                                        </button>
                                    </DropdownMenu.Item>
                                );
                            })}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    </div>
                ) : (
                    <div className="user-chat-conversation-label">{activeConversationTitle}</div>
                )}
                <div className='user-chat-header-actions mr-2'>
                    {selectedMovieId !== null && (
                    <Tooltip content={t('aiChat.newChatTooltip')}>
                        <button
                            type="button"
                            className="user-chat-header-action"
                            aria-label={t('aiChat.newChatTooltip')}
                            onClick={handleNewChat}
                        >
                            <PlusIcon aria-hidden="true" />
                        </button>
                    </Tooltip>
                    )}
                    <Tooltip content={aiChatDebugMode ? t('aiChat.debugOn') : t('aiChat.debugOff')}>
                        <button
                            type="button"
                            className={`user-chat-header-action${aiChatDebugMode ? ' is-active' : ''}`}
                            aria-pressed={aiChatDebugMode}
                            aria-label={aiChatDebugMode ? t('aiChat.debugOn') : t('aiChat.debugOff')}
                            onClick={toggleAiChatDebugMode}
                        >
                            <CodeIcon aria-hidden="true" />
                        </button>
                    </Tooltip>
                </div>
            </div>
            {selectedMovieId === null && !isLoadingChat && aiChatHistory.length === 0 && aiChatText.trim() === '' && <div className="ai-welcome-message m-3">
                <Callout.Root variant="soft" color="gray">
                    <Callout.Icon>
                        <InfoCircledIcon />
                    </Callout.Icon>
                    <Callout.Text>
                        <AIAssistantMessage />
                    </Callout.Text>
                </Callout.Root>
            </div>}
            <ScrollArea size="2" type="auto" className="user-chat-content" ref={scrollAreaRef}>
                {isLoadingChat && (
                    <div className='chat-loading-container'>
                        <div className='chat-loading-spinner' />
                        <span className='text-theme-muted text-sm mt-2'>
                            {isEnsuringMovie ? 'Creating project...' : 'Loading chat...'}
                        </span>
                    </div>
                )}
                {chatError && (
                    <Callout.Root variant="soft" color="red" className="m-2">
                        <Callout.Text>{chatError}</Callout.Text>
                    </Callout.Root>
                )}
                {selectedMovieId === null && !isLoadingChat && aiChatHistory.length === 0 && (
                    <div className='flex gap-2'>
                        <p className='text-theme-secondary p-2'>Here are some sample prompts to get you started:</p>
                        <div className='flex flex-wrap gap-1'>
                            {aiSamplePrompts.map((prompt: string) => (
                                <Card key={generateId('sample-prompt')}
                                    variant="surface"
                                    className='cursor-pointer m-1 hover:brightness-125'
                                    onClick={() => setAiChatText(prompt)}
                                >
                                    <Text as="p" size="3">
                                        <QuoteIcon className='w-5 h-5 text-theme-secondary' />
                                        <Text size="3" className='text-theme-muted'>{prompt}</Text>
                                    </Text>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}
                {aiChatHistory.map((message: ChatMessage) => (
                    <ChatMessageItem
                        key={message.id}
                        message={message}
                        showDebug={showDebug}
                        onDelete={aiChatActiveConversationId !== null ? handleRequestDeleteMessage : undefined}
                        isDeleting={deletingMessageId === message.id}
                    />
                ))}
                {isSending && (
                    <div className='flex items-center gap-2 p-2'>
                        <PersonIcon className='w-4 h-4 text-theme-primary' />
                        <div className='typing-indicator'>
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                )}
                <div className="chat-prompt-walker-clearance" aria-hidden="true" />
                <div ref={messagesEndRef} />
            </ScrollArea>
            <div className="user-chat-inputs">
                <ChatPromptWalker />
                <TextArea
                    className="user-chat-textarea"
                    variant="surface"
                    size="2"
                    radius="medium"
                    value={aiChatText}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAiChatText(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void sendPromptToAI();
                        }
                    }}
                    rows={calculateRows(aiChatText)}
                    placeholder={t('aiChat.placeholder')}
                    autoFocus
                />
                <div className="user-chat-input-actions">
                    <Button
                        className="user-chat-send-button"
                        variant="soft"
                        color="gray"
                        radius="medium"
                        size="2"
                        disabled={isSending || isEnsuringMovie || !aiChatText.trim()}
                        onClick={() => void sendPromptToAI()}
                    >
                        <PaperPlaneIcon aria-hidden="true" />
                        <span>{t('aiChat.send')}</span>
                    </Button>
                </div>
            </div>

            <ConfirmModal
                variant="panel"
                open={deleteConfirmMessageId !== null}
                onOpenChange={(open) => {
                    if (!open) setDeleteConfirmMessageId(null);
                }}
                title="Delete message?"
                description={
                    deleteConfirmPreview
                        ? `"${deleteConfirmPreview}${deleteConfirmPreview.length >= 120 ? '…' : ''}" will be permanently deleted.`
                        : 'This message will be permanently deleted.'
                }
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={() => void handleConfirmDeleteMessage()}
            />
        
        </div>
    );
}
